import {
  OperationVariables,
  QueryLazyOptions,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import { format } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { isSalesAgent } from 'utils/roles';

import { getOrganization, getSalesAgentTeam, getTeam } from 'queries/org';
import { getServices } from 'queries/services';
import { getIncompleteTasksByUserQuery } from 'queries/tasks';

import { Organization, Service, Team, TeamMember } from '../interfaces';
import { useSession } from './session';

export interface OrganizationContextValue {
  organization: Organization | undefined;
  team: TeamMember[] | undefined;
  services: Service[] | undefined;
  taskData: any | undefined;
  teamMember: (id: string) => TeamMember | undefined;
  loadServices: (
    options?: QueryLazyOptions<Record<string, any>> | undefined,
  ) => void;
  servicesCalled: boolean;
  loadTeam: (options?: QueryLazyOptions<OperationVariables>) => void;
}

const initialState = {
  team: undefined,
};

const OrganizationContext = createContext(
  initialState as OrganizationContextValue,
);

interface State {
  team: TeamMember[] | undefined;
}

const OP = ({ children }: { children: React.ReactNode }) => {
  const { claims, user } = useSession();
  const salesAgent = isSalesAgent(claims);

  const [state, setState] = useState<State>({
    ...initialState,
  });

  const [loadFullTeam, fullTeam] = useLazyQuery<Team>(getTeam, {
    fetchPolicy: 'network-only',
  });
  const [loadAgentTeam, agentTeam] = useLazyQuery<Team>(getSalesAgentTeam, {
    fetchPolicy: 'network-only',
  });
  const loadTeam = salesAgent ? loadAgentTeam : loadFullTeam;
  const data = salesAgent ? agentTeam.data : fullTeam.data;
  const teamCalled = salesAgent ? agentTeam.called : fullTeam.called;

  const [loadOrganization, { data: organization, called: orgCalled }] =
    useLazyQuery<{ organization: Organization }>(getOrganization, {
      variables: {
        organization: claims && claims['x-hasura-organization-id'],
      },
      fetchPolicy: 'cache-and-network',
    });

  // console.log("organization", organization)

  const [loadServices, { data: serviceData, called: servicesCalled }] =
    useLazyQuery<{
      service: Service[];
    }>(getServices, {
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    const org = claims && claims['x-hasura-organization-id'];
    const role = claims && claims['x-hasura-role'];
    if (org && role) {
      if (!teamCalled) {
        loadTeam({
          variables: {
            organization: org,
          },
        });
      }
      if (!orgCalled) {
        loadOrganization({
          variables: {
            organization: org,
          },
        });
      }
    }
  }, [claims, teamCalled, orgCalled, loadTeam, loadOrganization]);

  const teamMember = useCallback(
    (id: string) => {
      if (!data) return undefined;
      return data.team.find((x) => x.user.id === id);
    },
    [data],
  );

  const member = user && teamMember(claims['x-hasura-user-id']);
  const taskUserId = member?.user?.id || claims?.['x-hasura-user-id'] || '';
  // Sales agents do not use the staff task inbox; never query without a concrete uuid.
  const skipTasks = salesAgent || !taskUserId;

  const { data: taskData } = useQuery(getIncompleteTasksByUserQuery, {
    variables: {
      id: taskUserId,
      target: format(new Date(), 'yyyy-MM-dd'),
    },
    pollInterval: skipTasks ? 0 : 1000 * 60,
    skip: skipTasks,
  });

  const value: OrganizationContextValue = useMemo(
    () => ({
      ...state,
      organization: organization && organization.organization,
      team: data && data.team,
      services: serviceData && serviceData.service,
      teamMember,
      loadServices,
      servicesCalled,
      loadTeam,
      taskData,
    }),
    [
      data,
      loadServices,
      loadTeam,
      organization,
      serviceData,
      servicesCalled,
      state,
      teamMember,
      taskData,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

const OrganizationProvider = OP;

export const useOrganization = () => useContext(OrganizationContext);

export default OrganizationProvider;
